import React, { useState, useEffect } from 'react';
import ClientList from './components/ClientList';
import AddClient from './components/AddClient';
import RoutePlanner from './components/RoutePlanner';
import Reports from './components/Reports';
import Login from './components/Login';
import ResetPassword from './components/ResetPassword';
import RouteCalendar from './components/RouteCalendar';
import Billing from './components/Billing';
import AdminConsole from './components/AdminConsole';
import Team from './components/Team';
import Profile from './components/Profile';
import { getClients, getMe, listLocations, openBillingPortal, isSubscriptionRequiredError } from './components/api';
import { useTranslation } from './i18n';
import LangToggle from './components/LangToggle';

// ─── Icons ───────────────────────────────────────────────────────────────────
const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const PinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const ChartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);
const TeamIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="19" y1="8" x2="19" y2="14" />
    <line x1="22" y1="11" x2="16" y2="11" />
  </svg>
);
const HamburgerIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const ChevronIcon = ({ direction = 'right' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {direction === 'left'
      ? <polyline points="15 18 9 12 15 6" />
      : <polyline points="9 18 15 12 9 6" />}
  </svg>
);
const GearIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);
const CardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="5" width="20" height="14" rx="2" ry="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
  </svg>
);
const LogoutIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);
const UserCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="10" r="3" />
    <path d="M6.5 19a6 6 0 0 1 11 0" />
  </svg>
);
const SymbolWhite = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none" aria-hidden="true">
    <polyline points="16,64 16,20 40,46 64,20 64,64" stroke="#ffffff" strokeWidth="6.5" strokeLinecap="round" strokeLinejoin="round" fill="none" strokeDasharray="0.1 11" opacity="0.85" />
    <circle cx="16" cy="64" r="5" fill="#ffffff" opacity="0.85" />
    <circle cx="40" cy="46" r="4" fill="#ffffff" opacity="0.85" />
    <circle cx="64" cy="64" r="4" fill="#ffffff" opacity="0.85" />
    <path d="M55.9,20.5 L64,35.35 L72.1,20.5 Z" fill="#ffffff" />
    <circle cx="64" cy="16" r="9" fill="#ffffff" />
  </svg>
);

// ─── Styles ──────────────────────────────────────────────────────────────────
const shellCSS = `
  .ms-shell {
    display: flex;
    min-height: 100vh;
    background: #f7f9f4;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    color: #1a1a1a;
  }

  /* Sidebar (desktop) */
  .ms-sidebar {
    width: 90px;
    background: #1f9d55;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 18px 0 22px;
    position: sticky;
    top: 0;
    height: 100vh;
    box-sizing: border-box;
    transition: width 0.22s ease, padding 0.22s ease;
  }
  .ms-sidebar.expanded {
    width: 220px;
    align-items: stretch;
    padding: 18px 14px 22px;
  }
  .ms-sidebar-brand {
    width: 56px; height: 56px; border-radius: 14px;
    background: rgba(255,255,255,0.10);
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 28px;
    flex-shrink: 0;
    transition: width 0.22s ease, padding 0.22s ease, gap 0.22s ease, justify-content 0.22s ease;
  }
  .ms-sidebar.expanded .ms-sidebar-brand {
    width: auto;
    height: 48px;
    justify-content: flex-start;
    padding: 0 10px;
    gap: 10px;
  }
  .ms-sidebar-brand-text {
    font-family: 'Manrope', -apple-system, BlinkMacSystemFont, sans-serif;
    font-weight: 800;
    font-size: 18px;
    letter-spacing: -0.4px;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s 0.08s ease;
  }
  .ms-sidebar.expanded .ms-sidebar-brand-text { opacity: 1; }

  .ms-sidebar-nav {
    flex: 1;
    display: flex; flex-direction: column; align-items: center; gap: 6px;
    width: 100%;
  }
  .ms-sidebar.expanded .ms-sidebar-nav { align-items: stretch; }
  .ms-nav-btn {
    width: 52px; height: 52px;
    border-radius: 12px;
    background: transparent; border: none;
    color: rgba(255,255,255,0.7);
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background .15s, color .15s, width 0.22s ease, padding 0.22s ease;
    position: relative;
    font-family: inherit;
  }
  .ms-sidebar.expanded .ms-nav-btn {
    width: 100%;
    justify-content: flex-start;
    padding: 0 14px;
    gap: 14px;
    font-size: 14px;
    font-weight: 600;
  }
  .ms-nav-btn:hover { background: rgba(255,255,255,0.10); color: #fff; }
  .ms-nav-btn.active { background: rgba(255,255,255,0.18); color: #fff; }
  .ms-nav-btn svg { width: 22px; height: 22px; flex-shrink: 0; }
  .ms-nav-label { display: none; white-space: nowrap; }
  .ms-sidebar.expanded .ms-nav-label { display: inline; }
  .ms-nav-badge {
    position: absolute; top: 5px; right: 5px;
    background: #fff; color: #14653a;
    font-size: 10px; font-weight: 800;
    border-radius: 100px; min-width: 17px; height: 17px;
    display: flex; align-items: center; justify-content: center;
    padding: 0 4px; line-height: 1;
  }
  .ms-sidebar.expanded .ms-nav-badge {
    position: static;
    margin-left: auto;
  }
  /* Account button (bottom of sidebar, under the nav) */
  .ms-account-btn {
    display: flex; align-items: center; gap: 12px;
    width: 100%;
    border: none; background: transparent; cursor: pointer;
    border-radius: 12px;
    padding: 6px;
    justify-content: center;
    transition: background .15s;
    font-family: inherit;
    margin-top: 6px;
  }
  .ms-account-btn:hover { background: rgba(255,255,255,0.10); }
  .ms-account-btn.active { background: rgba(255,255,255,0.18); }
  .ms-sidebar.expanded .ms-account-btn { justify-content: flex-start; padding: 8px; }
  .ms-account-icon {
    width: 40px; height: 40px; border-radius: 11px;
    background: rgba(255,255,255,0.14);
    color: #fff;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .ms-account-icon svg { width: 22px; height: 22px; }
  .ms-account-meta {
    display: none;
    flex-direction: column; align-items: flex-start;
    min-width: 0; line-height: 1.3;
  }
  .ms-sidebar.expanded .ms-account-meta { display: flex; }
  .ms-account-name {
    color: #fff; font-size: 13.5px; font-weight: 700;
    white-space: normal; overflow-wrap: anywhere; word-break: break-word;
  }
  .ms-account-sub {
    color: rgba(255,255,255,0.7); font-size: 11px; font-weight: 600;
  }

  /* Sidebar expand/collapse toggle — a clean handle centered on the right edge */
  .ms-sidebar-toggle {
    position: absolute;
    right: -13px;
    top: 50%;
    transform: translateY(-50%);
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: #fff;
    border: 1px solid #d8e0d3;
    color: #14653a;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(20,101,58,0.18);
    z-index: 20;
    padding: 0;
    transition: background 0.15s, color 0.15s, box-shadow 0.15s;
  }
  .ms-sidebar-toggle:hover {
    background: #ecf8ee;
    box-shadow: 0 3px 10px rgba(20,101,58,0.26);
  }
  .ms-sidebar-toggle svg { width: 15px; height: 15px; }

  /* Main */
  .ms-main {
    flex: 1; min-width: 0;
    display: flex; flex-direction: column;
  }

  /* Top bar */
  .ms-topbar {
    background: #fff;
    border-bottom: 1px solid #e3e9df;
    padding: 14px 28px;
    display: flex; align-items: center; justify-content: space-between;
    gap: 16px;
    position: sticky; top: 0; z-index: 50;
  }
  .ms-topbar-left {
    display: flex; align-items: center; gap: 12px; min-width: 0;
  }
  .ms-brand-wordmark {
    font-family: 'Manrope', -apple-system, BlinkMacSystemFont, sans-serif;
    font-weight: 800; font-size: 20px; letter-spacing: -0.4px; line-height: 1;
    white-space: nowrap;
  }
  .ms-brand-mow { color: #14653a; }
  .ms-brand-stop { color: #1f9d55; }
  .ms-breadcrumb {
    font-size: 14px; color: #5f6b58; font-weight: 500;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .ms-location-switcher {
    margin-left: 4px;
    padding: 6px 10px;
    border: 1px solid #e3e9df;
    border-radius: 8px;
    background: #f7f9f4;
    color: #14653a;
    font-size: 13px;
    font-weight: 700;
    font-family: inherit;
    cursor: pointer;
    max-width: 200px;
  }
  .ms-topbar-right {
    display: flex; align-items: center; gap: 8px; flex-shrink: 0;
  }
  .ms-billing-btn, .ms-logout-btn {
    padding: 7px 14px;
    border: 1px solid #e3e9df;
    background: #fff;
    border-radius: 8px;
    font-size: 13px; font-weight: 600;
    cursor: pointer;
    font-family: inherit;
  }
  .ms-billing-btn { color: #14653a; }
  .ms-logout-btn { color: #5f6b58; font-weight: 500; }

  /* Hamburger button (mobile only) */
  .ms-hamburger {
    display: none;
    width: 40px; height: 40px;
    border: 1px solid #e3e9df;
    background: #fff;
    border-radius: 10px;
    color: #1a1a1a;
    cursor: pointer;
    align-items: center; justify-content: center;
  }
  .ms-hamburger svg { width: 22px; height: 22px; }

  /* Banners */
  .ms-trial-banner, .ms-past-due-banner {
    padding: 9px 28px; font-size: 13px; text-align: center;
  }
  .ms-trial-banner { background: #FEF3C7; color: #92400E; }
  .ms-past-due-banner { background: #FEE2E2; color: #991B1B; }
  .ms-banner-link {
    background: none; border: none;
    color: inherit; font-weight: 700; text-decoration: underline;
    cursor: pointer; font-size: inherit; padding: 0;
  }

  /* Content */
  .ms-content {
    padding: 24px 28px;
    flex: 1;
    width: 100%;
    box-sizing: border-box;
    max-width: 1180px;
    align-self: center;
  }
  .ms-section-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 16px; gap: 12px;
  }
  .ms-section-title {
    font-size: 16px; font-weight: 700; color: #1a1a1a;
    font-family: 'Manrope', -apple-system, sans-serif;
    letter-spacing: -0.2px;
  }
  .ms-add-btn {
    padding: 9px 18px;
    border: none; background: #14653a;
    border-radius: 8px;
    font-size: 14px; font-weight: 700; color: #fff;
    cursor: pointer;
    font-family: inherit;
  }
  .ms-add-btn:hover { background: #0f4d2c; }
  .ms-loading { text-align: center; padding: 40px; color: #5f6b58; }

  /* Mobile menu drawer */
  .ms-menu-overlay {
    position: fixed; inset: 0;
    background: rgba(15, 25, 12, 0.45);
    z-index: 200;
    display: flex; justify-content: flex-end;
    animation: ms-fade .18s ease;
  }
  @keyframes ms-fade { from { opacity: 0; } to { opacity: 1; } }
  .ms-menu-drawer {
    width: 300px; max-width: 88%;
    background: #fff;
    height: 100%;
    display: flex; flex-direction: column;
    box-shadow: -10px 0 30px rgba(0,0,0,0.18);
    animation: ms-slide .25s cubic-bezier(.2,.8,.2,1);
    overflow-y: auto;
  }
  @keyframes ms-slide { from { transform: translateX(100%); } to { transform: translateX(0); } }
  .ms-menu-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 20px 16px;
    border-bottom: 1px solid #e3e9df;
  }
  .ms-menu-close {
    width: 38px; height: 38px;
    border-radius: 10px;
    background: none; border: 1px solid #e3e9df;
    color: #5f6b58;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
  }
  .ms-menu-close svg { width: 18px; height: 18px; }
  .ms-menu-items { padding: 10px; display: flex; flex-direction: column; gap: 2px; }
  .ms-menu-section-label {
    font-size: 11px; font-weight: 700; letter-spacing: 0.06em;
    text-transform: uppercase; color: #5f6b58;
    padding: 14px 12px 6px;
  }
  .ms-menu-item {
    display: flex; align-items: center; gap: 14px;
    padding: 13px 12px;
    background: none; border: none;
    color: #1a1a1a;
    font-size: 15px; font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    text-align: left;
    border-radius: 10px;
    width: 100%;
  }
  .ms-menu-item:hover { background: #f3f4ef; }
  .ms-menu-item.active { background: #ecf8ee; color: #14653a; }
  .ms-menu-item svg { width: 20px; height: 20px; flex-shrink: 0; }
  .ms-menu-item.danger { color: #b91c1c; }
  .ms-menu-item-label { flex: 1; }
  .ms-menu-badge {
    margin-left: auto;
    background: #14653a; color: #fff;
    font-size: 11px; font-weight: 700;
    border-radius: 100px; padding: 2px 8px;
    line-height: 1.4;
  }
  .ms-menu-divider {
    height: 1px; background: #e3e9df; margin: 8px 12px;
  }

  /* Mobile breakpoint */
  @media (max-width: 767px) {
    .ms-sidebar { display: none; }
    .ms-topbar { padding: 12px 16px; gap: 10px; }
    .ms-brand-wordmark { font-size: 19px; }
    .ms-breadcrumb { display: none; }
    .ms-billing-btn, .ms-logout-btn { display: none; }
    .ms-hamburger { display: flex; }
    .ms-content { padding: 16px; }
    .ms-trial-banner, .ms-past-due-banner { padding: 9px 16px; font-size: 12px; }
  }
`;

// ─── Component ───────────────────────────────────────────────────────────────
export default function App() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [routeDate, setRouteDate] = useState(null); // date handed from Calendar → Route tab
  const [routeClientIds, setRouteClientIds] = useState([]);
  const [routeClients, setRouteClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState(null);
  const [token, setToken] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [needsUpgrade, setNeedsUpgrade] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [role, setRole] = useState('owner');
  const [displayName, setDisplayName] = useState('');
  const [locations, setLocations] = useState([]);
  const [activeLocationId, setActiveLocationId] = useState(
    () => localStorage.getItem('activeLocationId') || ''
  );
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    try {
      return window.localStorage.getItem('ms-sidebar-expanded') === 'true';
    } catch (e) {
      return false;
    }
  });

  // Persist sidebar expanded preference.
  useEffect(() => {
    try {
      window.localStorage.setItem('ms-sidebar-expanded', String(sidebarExpanded));
    } catch (e) { /* ignore */ }
  }, [sidebarExpanded]);

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedBusiness = localStorage.getItem('business');
    if (savedToken && savedBusiness) {
      setToken(savedToken);
      setBusiness(JSON.parse(savedBusiness));
    }
    setAuthLoading(false);
  }, []);

  // Handle return from Stripe Checkout / Portal.
  useEffect(() => {
    if (!token || !business) return;
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (!checkout) return;

    // Clean the URL so a refresh doesn't re-trigger this.
    window.history.replaceState({}, '', window.location.pathname);

    if (checkout === 'cancel') {
      setNeedsUpgrade(true);
      return;
    }

    // checkout === 'success' — poll /auth/me until the webhook lands.
    let attempts = 0;
    const isActive = (biz) => {
      if (!biz) return false;
      const s = biz.subscription_status;
      if (s === 'active' || s === 'trialing' || s === 'past_due') return true;
      if (s === 'trial' && biz.trial_ends_at && new Date(biz.trial_ends_at) > new Date()) return true;
      return false;
    };
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await getMe();
        const biz = data.business;
        localStorage.setItem('business', JSON.stringify(biz));
        setBusiness(biz);
        if (isActive(biz)) {
          setNeedsUpgrade(false);
          return;
        }
      } catch (err) {
        // ignore, retry below
      }
      if (attempts < 6) setTimeout(poll, 1500);
    };
    poll();
  }, [token, business]);

  // Load the business's locations and settle on an active one. The active
  // location scopes Clients / Route / Calendar (sent as x-location-id by the
  // api interceptor). Managers & crew are forced server-side regardless.
  useEffect(() => {
    if (!token || !business) return;
    let cancelled = false;
    listLocations()
      .then((res) => {
        if (cancelled) return;
        const locs = res.data || [];
        setLocations(locs);
        setActiveLocationId((prev) => {
          const stillValid = prev && locs.some((l) => l.id === prev);
          const next = stillValid ? prev : (locs[0]?.id || '');
          if (next) localStorage.setItem('activeLocationId', next);
          return next;
        });
      })
      .catch(() => { /* ignore — single-location/no-location accounts still work */ });
    return () => { cancelled = true; };
  }, [token, business]);

  // (Re)load clients whenever the business or the active location changes.
  useEffect(() => {
    if (business) loadClients();
  }, [business, activeLocationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Once logged in, ask /auth/me whether this user is a platform admin so we
  // can reveal the Partners page. Silently ignores failures — non-admins
  // simply don't see the menu item.
  useEffect(() => {
    if (!token || !business) return;
    let cancelled = false;
    getMe()
      .then((res) => {
        if (cancelled) return;
        setIsAdmin(!!res.data?.is_admin);
        if (res.data?.role) setRole(res.data.role);
        setDisplayName(
          res.data?.user?.user_metadata?.display_name || res.data?.member?.member_name || ''
        );
      })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [token, business]);

  // Crew managers only have Route + Account — keep them off other tabs.
  useEffect(() => {
    if (role === 'crew_manager' && tab !== 'route' && tab !== 'account') {
      setTab('route');
    }
  }, [role, tab]);

  // Close the mobile menu on Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const loadClients = async () => {
    try {
      const { data } = await getClients();
      setClients(data);
      setNeedsUpgrade(false);
    } catch (err) {
      if (isSubscriptionRequiredError(err)) {
        setNeedsUpgrade(true);
      } else {
        console.error('Failed to load clients', err);
      }
    }
    setLoading(false);
  };

  const handleLogin = (biz, tok) => {
    setBusiness(biz);
    setToken(tok);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('business');
    setBusiness(null);
    setToken(null);
    setClients([]);
    setRouteClients([]);
    setRouteClientIds([]);
    setNeedsUpgrade(false);
    setTab('clients');
    setMenuOpen(false);
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const { data } = await openBillingPortal();
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
    } catch (err) {
      alert(err.response?.data?.error || t('app.portal_error'));
    }
    setPortalLoading(false);
  };

  const handleClientAdded = (client) => {
    setClients([...clients, client]);
    setShowAdd(false);
  };
  const handleClientDeleted = (id) => {
    setClients(clients.filter((c) => c.id !== id));
    setRouteClientIds(routeClientIds.filter((rid) => rid !== id));
    setRouteClients(routeClients.filter((c) => c.id !== id));
  };
  const handleClientUpdated = (updated) => {
    setClients(clients.map((c) => (c.id === updated.id ? updated : c)));
  };
  const handleAddToRoute = (client) => {
    if (routeClientIds.includes(client.id)) {
      setRouteClientIds(routeClientIds.filter((id) => id !== client.id));
      setRouteClients(routeClients.filter((c) => c.id !== client.id));
      return;
    }
    setRouteClientIds([...routeClientIds, client.id]);
    setRouteClients([...routeClients, client]);
  };
  const handleRemoveFromRoute = (clientId) => {
    setRouteClientIds(routeClientIds.filter((id) => id !== clientId));
    setRouteClients(routeClients.filter((c) => c.id !== clientId));
  };
  const handleClearRoute = () => {
    setRouteClientIds([]);
    setRouteClients([]);
  };
  const handleRouteEnded = () => {
    setRouteClientIds([]);
    setRouteClients([]);
  };

  const goToTab = (next) => {
    setTab(next);
    setMenuOpen(false);
  };

  // The password-reset page (reached from the reset email) renders standalone,
  // even when signed out and before auth resolves.
  if (typeof window !== 'undefined' && window.location.pathname === '/reset-password') {
    return <ResetPassword />;
  }

  if (authLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
      }}>
        <div style={{ fontSize: 14, color: '#5f6b58' }} role="status" aria-live="polite">{t('app.loading_dots')}</div>
      </div>
    );
  }

  if (!business || !token) {
    return <Login onLogin={handleLogin} />;
  }

  // Master admin (ltl@mowstop.com) gets a dedicated console instead of the
  // landscaping app — no sidebar, just Tenants / Partners / Settings.
  if (isAdmin) {
    return <AdminConsole onSignOut={handleLogout} />;
  }

  if (needsUpgrade) {
    return <Billing business={business} onSignOut={handleLogout} />;
  }

  const trialDaysLeft = business.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(business.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;
  const isTrialing = business.subscription_status === 'trial' || business.subscription_status === 'trialing';
  const isPastDue = business.subscription_status === 'past_due';

  // Crew managers run their assigned routes only — they get a single Route tab.
  const isCrewManager = role === 'crew_manager';
  const NAV = isCrewManager
    ? [{ key: 'route', label: t('app.route'), icon: <PinIcon />, badge: routeClientIds.length }]
    : [
        { key: 'clients', label: t('app.clients'), icon: <UsersIcon />, badge: 0 },
        { key: 'route', label: t('app.route'), icon: <PinIcon />, badge: routeClientIds.length },
        { key: 'calendar', label: t('app.calendar'), icon: <CalendarIcon />, badge: 0 },
        { key: 'reports', label: t('app.reports'), icon: <ChartIcon />, badge: 0 },
        ...(role === 'owner' || role === 'manager'
          ? [{ key: 'team', label: t('app.team'), icon: <TeamIcon />, badge: 0 }]
          : [])
      ];
  const TAB_LABEL = {
    clients: t('app.clients'),
    route: t('app.route'),
    calendar: t('app.calendar'),
    reports: t('app.reports'),
    team: t('app.team'),
    account: t('app.account'),
  };
  const accountName = displayName || business.name || '';

  return (
    <>
      <style>{shellCSS}</style>
      <a href="#ms-main-content" className="ms-skip-link">{t('app.skip_to_main')}</a>
      <div className="ms-shell">
        {/* Sidebar (desktop) */}
        <aside className={`ms-sidebar${sidebarExpanded ? ' expanded' : ''}`}>
          <button
            className="ms-sidebar-toggle"
            onClick={() => setSidebarExpanded((v) => !v)}
            aria-label={sidebarExpanded ? t('app.collapse_sidebar') : t('app.expand_sidebar')}
            title={sidebarExpanded ? t('app.collapse_sidebar') : t('app.expand_sidebar')}
          >
            <ChevronIcon direction={sidebarExpanded ? 'left' : 'right'} />
          </button>
          <div className="ms-sidebar-brand">
            <SymbolWhite size={28} />
            <span className="ms-sidebar-brand-text">MowStop</span>
          </div>
          <div className="ms-sidebar-nav">
            {NAV.map((item) => (
              <button
                key={item.key}
                className={`ms-nav-btn${tab === item.key ? ' active' : ''}`}
                onClick={() => setTab(item.key)}
                title={item.label}
                aria-label={item.label}
              >
                {item.icon}
                <span className="ms-nav-label">{item.label}</span>
                {item.badge > 0 && <span className="ms-nav-badge">{item.badge}</span>}
              </button>
            ))}
          </div>
          <button
            className={`ms-account-btn${tab === 'account' ? ' active' : ''}`}
            onClick={() => setTab('account')}
            title={accountName}
            aria-label={t('app.account')}
          >
            <span className="ms-account-icon"><GearIcon /></span>
            <div className="ms-account-meta">
              <span className="ms-account-name">{accountName}</span>
              <span className="ms-account-sub">{t('app.account')}</span>
            </div>
          </button>
        </aside>

        {/* Main */}
        <main className="ms-main">
          <header className="ms-topbar">
            <div className="ms-topbar-left">
              <span className="ms-brand-wordmark">
                <span className="ms-brand-mow">Mow</span>
                <span className="ms-brand-stop">Stop</span>
              </span>
              <span className="ms-breadcrumb">/ {TAB_LABEL[tab]}</span>
              {role === 'owner' && locations.length > 1 && (
                <select
                  className="ms-location-switcher"
                  value={activeLocationId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setActiveLocationId(id);
                    localStorage.setItem('activeLocationId', id);
                  }}
                  title="Active location"
                >
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>📍 {l.name}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="ms-topbar-right">
              {business.stripe_customer_id && (
                <button
                  className="ms-billing-btn"
                  onClick={handleManageBilling}
                  disabled={portalLoading}
                  title={t('app.manage_billing_title')}
                >
                  {portalLoading ? '…' : t('app.billing')}
                </button>
              )}
              <button className="ms-logout-btn" onClick={handleLogout}>{t('app.sign_out')}</button>
              <button
                className="ms-hamburger"
                onClick={() => setMenuOpen(true)}
                aria-label={t('app.open_menu')}
              >
                <HamburgerIcon />
              </button>
            </div>
          </header>

          {isPastDue && (
            <div className="ms-past-due-banner">
              {t('app.past_due_prefix')} <button className="ms-banner-link" onClick={handleManageBilling}>{t('app.update_your_card')}</button>
            </div>
          )}

          {!isPastDue && isTrialing && trialDaysLeft <= 7 && (
            <div className="ms-trial-banner">
              {t(trialDaysLeft === 1 ? 'app.trial_days_left_one' : 'app.trial_days_left_many', { n: trialDaysLeft })}
            </div>
          )}

          <div className="ms-content" id="ms-main-content" tabIndex="-1">
            {tab === 'clients' && (
              <div>
                {clients.length > 150 && (
                  <div style={{ background: '#FEF3C7', color: '#92400E', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>
                    {t('app.plan_limit_warning', { n: clients.length })}
                  </div>
                )}
                <div className="ms-section-header">
                  <span className="ms-section-title">{t('app.all_clients')}</span>
                  <button className="ms-add-btn" onClick={() => setShowAdd(true)}>{t('app.add_client')}</button>
                </div>
                {loading ? (
                  <div className="ms-loading" role="status" aria-live="polite">{t('app.loading_clients')}</div>
                ) : (
                  <ClientList
                    clients={clients}
                    onClientDeleted={handleClientDeleted}
                    onClientUpdated={handleClientUpdated}
                    onAddToRoute={handleAddToRoute}
                    routeClientIds={routeClientIds}
                  />
                )}
              </div>
            )}
            {tab === 'route' && (
              <RoutePlanner
                key={activeLocationId}
                role={role}
                routeClients={routeClients}
                onRouteEnded={handleRouteEnded}
                onRemoveFromRoute={handleRemoveFromRoute}
                onClearRoute={handleClearRoute}
                initialDate={routeDate}
              />
            )}
            {tab === 'calendar' && (
              <RouteCalendar
                key={activeLocationId}
                role={role}
                onDateChange={(d) => setRouteDate(d)}
                onOpenRoute={(d) => { setRouteDate(d); setTab('route'); }}
              />
            )}
            {tab === 'reports' && <Reports role={role} locations={locations} />}
            {tab === 'team' && (role === 'owner' || role === 'manager') && <Team role={role} />}
            {tab === 'account' && (
              <Profile onProfileUpdated={({ display_name }) => setDisplayName(display_name)} />
            )}
          </div>
        </main>

        {/* Mobile menu drawer */}
        {menuOpen && (
          <div
            className="ms-menu-overlay"
            onClick={() => setMenuOpen(false)}
            role="presentation"
          >
            <div
              className="ms-menu-drawer"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={t('app.menu_navigate')}
            >
              <div className="ms-menu-header">
                <span className="ms-brand-wordmark">
                  <span className="ms-brand-mow">Mow</span>
                  <span className="ms-brand-stop">Stop</span>
                </span>
                <button
                  className="ms-menu-close"
                  onClick={() => setMenuOpen(false)}
                  aria-label={t('app.close_menu')}
                >
                  <CloseIcon />
                </button>
              </div>
              <div className="ms-menu-items">
                <div className="ms-menu-section-label">{t('app.menu_navigate')}</div>
                {NAV.map((item) => (
                  <button
                    key={item.key}
                    className={`ms-menu-item${tab === item.key ? ' active' : ''}`}
                    onClick={() => goToTab(item.key)}
                  >
                    {item.icon}
                    <span className="ms-menu-item-label">{item.label}</span>
                    {item.badge > 0 && <span className="ms-menu-badge">{item.badge}</span>}
                  </button>
                ))}
                <div className="ms-menu-divider" />
                <div className="ms-menu-section-label">{t('app.menu_account')}</div>
                <button
                  className={`ms-menu-item${tab === 'account' ? ' active' : ''}`}
                  onClick={() => goToTab('account')}
                >
                  <UserCircleIcon />
                  <span className="ms-menu-item-label">{accountName || t('app.account')}</span>
                </button>
                {business.stripe_customer_id && (
                  <button
                    className="ms-menu-item"
                    onClick={() => { setMenuOpen(false); handleManageBilling(); }}
                    disabled={portalLoading}
                  >
                    <CardIcon />
                    <span className="ms-menu-item-label">{portalLoading ? t('app.opening_dots') : t('app.billing')}</span>
                  </button>
                )}
                <button
                  className="ms-menu-item danger"
                  onClick={handleLogout}
                >
                  <LogoutIcon />
                  <span className="ms-menu-item-label">{t('app.sign_out')}</span>
                </button>

                <div className="ms-menu-divider" />
                <div className="ms-menu-section-label">{t('app.menu_language')}</div>
                <div style={{ padding: '8px 12px 12px' }}>
                  <LangToggle />
                </div>
              </div>
            </div>
          </div>
        )}

        {showAdd && (
          <AddClient
            onClientAdded={handleClientAdded}
            onCancel={() => setShowAdd(false)}
          />
        )}
      </div>
    </>
  );
}
